// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Gestión de Plantillas de Documentos
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Edit2, Eye, Trash2, RefreshCw, FileText,
  Code, CheckCircle, XCircle, Zap, Info, User,
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, List,
} from 'lucide-react';
import { documentTemplatesAPI } from '../../../api/documentTemplates';
import { employeesAPI } from '../../../api/employees';

const TYPE_OPTIONS = [
  { value: 'CERT_LABORAL', label: 'Carta Laboral' },
  { value: 'CERT_BANCO',   label: 'Carta para Banco' },
  { value: 'CERT_VISA',    label: 'Carta para Visa / Embajada' },
  { value: 'CUSTOM',       label: 'Personalizada' },
];

const AVAILABLE_VARS = [
  { key: 'employee_name', label: 'Nombre del empleado' },
  { key: 'employee_code', label: 'Código del empleado' },
  { key: 'cedula',        label: 'Cédula' },
  { key: 'position',      label: 'Cargo' },
  { key: 'department',    label: 'Departamento' },
  { key: 'salary',        label: 'Salario (número)' },
  { key: 'salary_words',  label: 'Salario (en letras)' },
  { key: 'hire_date',     label: 'Fecha de ingreso' },
  { key: 'company_name',  label: 'Nombre de la empresa' },
  { key: 'company_rnc',   label: 'RNC de la empresa' },
  { key: 'date',          label: 'Fecha actual' },
  { key: 'entidad_destino', label: 'Entidad destino' },
];

// ── Helpers barra de formato ──────────────────────────────────
function ToolBtn({ onClick, title, children, label }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={e => { e.preventDefault(); onClick(); }} // preventDefault evita perder el foco del textarea
      className="flex items-center justify-center w-7 h-7 rounded hover:bg-gray-200 text-gray-600 transition-colors font-semibold text-xs"
    >
      {children || label}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-gray-300 mx-0.5" />;
}

// ── Modal Editor ──────────────────────────────────────────────
function TemplateModal({ template, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:        template?.name        || '',
    type:        template?.type        || 'CERT_LABORAL',
    description: template?.description || '',
    content:     template?.content     || '',
    is_active:   template?.is_active   ?? true,
  });
  const [activePane, setActivePane]   = useState('editor'); // editor | preview
  const [preview, setPreview]         = useState('');
  const [previewEmpId, setPreviewEmpId] = useState('');
  const [empleados, setEmpleados]     = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const textareaRef                   = useRef(null);

  const isEdit = !!template;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Cargar empleados para el selector de preview
  useEffect(() => {
    employeesAPI.getAll({ estado: 'activo', limit: 500 })
      .then(r => setEmpleados(r.employees || r.data || []))
      .catch(() => {});
  }, []);

  const insertVar = (varKey) => {
    const tag = `{{${varKey}}}`;
    const el  = textareaRef.current;
    if (!el) { set('content', form.content + tag); return; }

    const start   = el.selectionStart;
    const end     = el.selectionEnd;
    const current = el.value; // siempre actualizado
    const newContent = current.slice(0, start) + tag + current.slice(end);
    set('content', newContent);

    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  // Envuelve el texto seleccionado con etiquetas HTML de formato
  const applyFormat = (openTag, closeTag) => {
    const el = textareaRef.current;
    if (!el) return;

    const start   = el.selectionStart;
    const end     = el.selectionEnd;
    const current = el.value; // leer del DOM, nunca del closure
    const inner   = current.slice(start, end);

    const newContent = current.slice(0, start) + openTag + inner + closeTag + current.slice(end);
    set('content', newContent);

    requestAnimationFrame(() => {
      el.focus();
      if (inner) {
        el.setSelectionRange(start + openTag.length, start + openTag.length + inner.length);
      } else {
        el.setSelectionRange(start + openTag.length, start + openTag.length);
      }
    });
  };

  // Prepara el contenido para renderizar en el preview
  const normalizeForPreview = (text) => {
    const hasBlockTags = /<(p|div|h[1-6]|ul|ol|li)[\s>]/i.test(text);
    if (hasBlockTags) {
      // Ya tiene estructura de bloque — solo asegurar que \n sueltos no colapsen
      return text.replace(/\n/g, ' ');
    }
    // Texto plano (con o sin inline HTML como <strong>, <em>, <u>)
    // Convertir cada línea en párrafo, preservando el HTML inline
    return text
      .split('\n')
      .map(line => line.trim() === '' ? '<br>' : `<p style="margin:0 0 0.75em 0">${line}</p>`)
      .join('');
  };

  const loadPreview = async (empId) => {
    setActivePane('preview');
    setPreviewLoading(true);
    try {
      const r = await documentTemplatesAPI.preview({
        content:     form.content,
        employee_id: empId || previewEmpId || undefined,
      });
      const resolved = r.resolved || form.content;
      setPreview(normalizeForPreview(resolved));
    } catch {
      setPreview(normalizeForPreview(form.content));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    if (!form.name.trim())    { setError('El nombre es requerido'); return; }
    if (!form.content.trim()) { setError('El contenido es requerido'); return; }

    setSaving(true);
    try {
      if (isEdit) {
        await documentTemplatesAPI.update(template.id, form);
      } else {
        await documentTemplatesAPI.create(form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-blue-600" />
            <h2 className="text-base font-bold text-gray-900">
              {isEdit ? 'Editar Plantilla' : 'Nueva Plantilla'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <XCircle size={16} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">

          {/* Campos base */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nombre *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => set('name', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                placeholder="Ej: Carta Laboral General"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo *</label>
              <select
                value={form.type}
                onChange={e => set('type', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
              >
                {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
              <input
                type="text"
                value={form.description}
                onChange={e => set('description', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                placeholder="Descripción breve de la plantilla..."
              />
            </div>
          </div>

          {/* Variables disponibles */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
            <p className="text-xs font-semibold text-blue-700 flex items-center gap-1.5 mb-2">
              <Code size={12} /> Variables disponibles — haz clic para insertar
            </p>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_VARS.map(v => (
                <button
                  key={v.key}
                  onClick={() => insertVar(v.key)}
                  className="text-xs px-2 py-0.5 bg-white border border-blue-200 text-blue-700 rounded-full hover:bg-blue-100 font-mono transition-colors"
                >
                  {`{{${v.key}}}`}
                </button>
              ))}
            </div>
          </div>

          {/* Editor / Preview tabs */}
          <div>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="flex gap-1">
                <button
                  onClick={() => setActivePane('editor')}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                    activePane === 'editor' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Edit2 size={11} /> Editor
                </button>
                <button
                  onClick={() => loadPreview()}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                    activePane === 'preview' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Eye size={11} /> Vista previa
                </button>
              </div>

              {/* Selector de empleado para preview */}
              {activePane === 'preview' && (
                <div className="flex items-center gap-1.5">
                  <User size={12} className="text-gray-400" />
                  <select
                    value={previewEmpId}
                    onChange={e => { setPreviewEmpId(e.target.value); loadPreview(e.target.value); }}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400 max-w-[200px]"
                  >
                    <option value="">— Sin datos reales —</option>
                    {empleados.map(e => (
                      <option key={e.id} value={e.id}>{e.nombre} {e.apellido}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {activePane === 'editor' ? (
              <div>
                {/* Barra de herramientas de formato */}
                <div className="flex items-center gap-0.5 mb-1 p-1.5 bg-gray-50 border border-gray-200 rounded-t-lg border-b-0 flex-wrap">
                  {/* Formato de texto */}
                  <ToolBtn title="Negrita" onClick={() => applyFormat('<strong>', '</strong>')}><Bold size={13} /></ToolBtn>
                  <ToolBtn title="Cursiva" onClick={() => applyFormat('<em>', '</em>')}><Italic size={13} /></ToolBtn>
                  <ToolBtn title="Subrayado" onClick={() => applyFormat('<u>', '</u>')}><Underline size={13} /></ToolBtn>
                  <Divider />
                  {/* Párrafos y saltos */}
                  <ToolBtn title="Párrafo" onClick={() => applyFormat('<p>', '</p>')} label="P" />
                  <ToolBtn title="Salto de línea" onClick={() => applyFormat('', '<br>')} label="↵" />
                  <Divider />
                  {/* Alineación */}
                  <ToolBtn title="Alinear izquierda"  onClick={() => applyFormat('<p style="text-align:left">',   '</p>')}><AlignLeft   size={13} /></ToolBtn>
                  <ToolBtn title="Centrar"             onClick={() => applyFormat('<p style="text-align:center">', '</p>')}><AlignCenter size={13} /></ToolBtn>
                  <ToolBtn title="Alinear derecha"     onClick={() => applyFormat('<p style="text-align:right">',  '</p>')}><AlignRight  size={13} /></ToolBtn>
                  <Divider />
                  {/* Lista */}
                  <ToolBtn title="Lista con viñetas" onClick={() => applyFormat('<ul>\n  <li>', '</li>\n</ul>')}><List size={13} /></ToolBtn>
                </div>

                <textarea
                  ref={textareaRef}
                  rows={12}
                  value={form.content}
                  onChange={e => set('content', e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-b-lg rounded-t-none px-3 py-2 resize-none focus:outline-none focus:border-blue-400 font-mono"
                  placeholder="Escribe el contenido de la plantilla...&#10;&#10;Ej: <p>Certificamos que {{employee_name}} labora en {{company_name}}...</p>"
                />
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <Info size={10} /> Selecciona texto y haz clic en un botón para aplicar formato
                </p>
              </div>
            ) : (
              <div className="min-h-[280px] border border-gray-200 rounded-lg bg-white overflow-hidden">
                {previewLoading ? (
                  <div className="flex items-center justify-center h-[280px]">
                    <div className="animate-spin h-5 w-5 rounded-full border-b-2 border-blue-600" />
                  </div>
                ) : (
                  <>
                    {/* Encabezado simulado del PDF */}
                    <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 text-center">
                      <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                        {previewEmpId
                          ? (empleados.find(e => e.id === previewEmpId)?.nombre_empresa || 'Empresa')
                          : 'Empresa'}
                      </p>
                      <p className="text-sm font-bold text-gray-800 mt-0.5">{form.name}</p>
                    </div>
                    <div
                      className="p-6 text-sm text-gray-800 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: preview || '<p class="text-gray-400 italic">Sin contenido para previsualizar</p>'
                      }}
                    />
                    {!previewEmpId && (
                      <div className="mx-6 mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-700 flex items-center gap-1">
                          <Info size={11} /> Selecciona un empleado arriba para ver las variables reemplazadas con datos reales.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => set('is_active', e.target.checked)}
                className="rounded"
              />
              Plantilla activa
            </label>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <CheckCircle size={14} />
            {saving ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Crear Plantilla')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────
export default function DocumentTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [initMsg, setInitMsg]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await documentTemplatesAPI.getAll();
      setTemplates(r.templates || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInitDefaults = async () => {
    try {
      const r = await documentTemplatesAPI.initDefaults();
      setInitMsg(r.message);
      load();
      setTimeout(() => setInitMsg(''), 4000);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeactivate = async (tpl) => {
    if (!window.confirm(`¿Desactivar la plantilla "${tpl.name}"?`)) return;
    try {
      await documentTemplatesAPI.deactivate(tpl.id);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (tpl) => { setEditing(tpl); setShowModal(true); };
  const openNew  = ()    => { setEditing(null); setShowModal(true); };
  const onSaved  = ()    => { setShowModal(false); setEditing(null); load(); };

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-gray-500">
          Define las plantillas HTML que se usan para generar cartas laborales automáticamente al aprobar solicitudes.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={handleInitDefaults}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50"
          >
            <Zap size={12} /> Plantillas por defecto
          </button>
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={12} /> Actualizar
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            <Plus size={12} /> Nueva Plantilla
          </button>
        </div>
      </div>

      {initMsg && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <CheckCircle size={14} /> {initMsg}
        </div>
      )}

      {/* Tabla */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin h-7 w-7 rounded-full border-b-2 border-blue-600" />
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl flex flex-col items-center justify-center py-16">
          <FileText size={36} className="text-gray-200 mb-3" />
          <p className="text-sm font-medium text-gray-500">No hay plantillas configuradas</p>
          <p className="text-xs text-gray-400 mt-1">Usa "Plantillas por defecto" para crear las 3 cartas básicas.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plantilla</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Variables</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {templates.map(tpl => (
                <tr key={tpl.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{tpl.name}</p>
                    {tpl.description && <p className="text-xs text-gray-400 mt-0.5">{tpl.description}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium">
                      {TYPE_OPTIONS.find(o => o.value === tpl.type)?.label || tpl.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-xs text-gray-500">
                      {(tpl.variables || []).length} variables
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {tpl.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                        <CheckCircle size={10} /> Activa
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                        <XCircle size={10} /> Inactiva
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(tpl)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                        title="Editar"
                      >
                        <Edit2 size={13} />
                      </button>
                      {tpl.is_active && (
                        <button
                          onClick={() => handleDeactivate(tpl)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                          title="Desactivar"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TemplateModal
          template={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
